// Target: src/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/UpdateProjectBillingSettingsSidebar/UpdateProjectBillingSettingsForm.tsx
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { setServerFieldErrors } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import useCurrencySelectOptions from "~/hooks/form/formOptionsData/useCurrencySelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateProjectBillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsApiDispatchers.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import BillingSettingsFormSections from "~/modules/billing/components/formSections/BillingSettingsFormSections.tsx";
import type { ProjectBillingSettingsFormData } from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import {
	createProjectBillingSettingsSchema,
	getProjectBillingSettingsDefaultValues,
	prepareProjectBillingSettingsPayload,
} from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type Props = {
	billingSettings: BillingSettings;
	clientId: string;
	onCancel: () => void;
	onSuccess: () => void;
};

const UpdateProjectBillingSettingsForm: React.FC<Props> = ({
	billingSettings,
	clientId,
	onCancel,
	onSuccess,
}) => {
	const [busy, setBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const schema = useMemo(() => createProjectBillingSettingsSchema(), []);

	const defaultValues = useMemo(
		() => getProjectBillingSettingsDefaultValues(billingSettings),
		[billingSettings],
	);

	const {
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		handleSubmit,
		setError,
		setValue,
		trigger,
		watch,
	} = useForm<ProjectBillingSettingsFormData>({
		defaultValues,
		mode: "onChange",
		resolver: yupResolver<ProjectBillingSettingsFormData>(schema),
	});

	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(
		clientId,
		billingSettings.clientContactPersonId ?? undefined,
	);
	const invoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(clientId);
	const currencySelectOptions = useCurrencySelectOptions();

	const formIsSubmittable = useFormIsSubmittable({
		enableInitially: false,
		isDirty,
		isLoading: busy,
		isSubmitted,
		isValid,
	});

	const isForeignCountry = watch("isForeignCountry");
	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	useEffect(() => {
		if (isSubmitted) {
			trigger("currencyId");
		}
	}, [isForeignCountry, isSubmitted, trigger]);

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const onSubmit: SubmitHandler<ProjectBillingSettingsFormData> = async (data) => {
		setBusy(true);
		try {
			const payload = prepareProjectBillingSettingsPayload(data);
			await updateProjectBillingSettings(billingSettings.ownerId, payload);
			onSuccess();
		} catch (error) {
			// The backend refuses a payload without exactly one recipient on the invoiceRecipientId field.
			if (!setServerFieldErrors(error, ["invoiceRecipientId"], setError)) {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setBusy(false);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungseinstellungen bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<BillingSettingsFormSections
						clientContactPersonSelectOptions={clientContactPersonSelectOptions}
						control={control}
						currencySelectOptions={currencySelectOptions}
						errors={errors}
						invoiceRecipientSelectOptions={invoiceRecipientSelectOptions}
						isSubmitted={isSubmitted}
						setValue={setValue}
						showCombineOrders={true}
						trigger={trigger}
						watch={watch}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateProjectBillingSettingsForm;